/**
 * ResponseRatePanel — response rate per sender (replied threads / total threads).
 */

import React from 'react';
import { Table, Typography, Progress, Tag, Avatar } from 'antd';
import { UserCheck } from 'lucide-react';

const { Text } = Typography;

function getInitials(email = '') {
  return email.slice(0, 2).toUpperCase();
}

function avatarColor(email) {
  const colors = ['#6366f1', '#22c55e', '#f59e0b', '#3b82f6', '#ef4444', '#8b5cf6', '#ec4899'];
  let hash = 0;
  for (const c of email) hash = c.charCodeAt(0) + hash * 31;
  return colors[Math.abs(hash) % colors.length];
}

function rateTag(rate) {
  if (rate >= 80) return <Tag color="green" style={{ borderRadius: 6, fontWeight: 600, fontSize: 11 }}>{rate}%</Tag>;
  if (rate >= 40) return <Tag color="orange" style={{ borderRadius: 6, fontWeight: 600, fontSize: 11 }}>{rate}%</Tag>;
  return <Tag color="red" style={{ borderRadius: 6, fontWeight: 600, fontSize: 11 }}>{rate}%</Tag>;
}

const columns = [
  {
    title: 'Sender',
    dataIndex: 'email',
    key: 'email',
    render: (email) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Avatar size={28} style={{ background: avatarColor(email), fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
          {getInitials(email)}
        </Avatar>
        <Text ellipsis style={{ maxWidth: 180, fontSize: 13 }}>{email}</Text>
      </div>
    ),
  },
  {
    title: 'Emails',
    dataIndex: 'totalReceived',
    key: 'totalReceived',
    width: 80,
    render: (v) => <Text type="secondary" style={{ fontSize: 13 }}>{v}</Text>,
  },
  {
    title: 'Replied',
    dataIndex: 'repliedThreads',
    key: 'repliedThreads',
    width: 80,
    render: (v) => <Text type="secondary" style={{ fontSize: 13 }}>{v}</Text>,
  },
  {
    title: 'Response Rate',
    key: 'rate',
    width: 200,
    render: (_, row) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Progress
          percent={row.rate}
          size="small"
          showInfo={false}
          strokeColor={row.rate >= 80 ? '#10b981' : row.rate >= 40 ? '#f59e0b' : '#ef4444'}
          trailColor="rgba(0,0,0,0.04)"
          style={{ flex: 1, marginBottom: 0 }}
        />
        {rateTag(row.rate)}
      </div>
    ),
  },
];

export function ResponseRatePanel({ metrics, isDark }) {
  const { responseRateBySender } = metrics;

  if (!responseRateBySender || responseRateBySender.length === 0) {
    return null;
  }

  const tableData = responseRateBySender.map((d, i) => ({ ...d, key: i }));

  return (
    <div style={{
      borderRadius: 14,
      background: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0'}`,
      boxShadow: isDark
        ? '0 1px 3px rgba(0,0,0,0.2)'
        : '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
      padding: '20px',
      marginBottom: 24,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8,
          background: isDark ? 'rgba(16,185,129,0.15)' : 'rgba(16,185,129,0.08)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <UserCheck size={16} color="#10b981" strokeWidth={2} />
        </div>
        <div>
          <Text strong style={{ fontSize: 14, display: 'block' }}>Response Rate by Sender</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>Who you're replying to vs ignoring</Text>
        </div>
      </div>
      <Table
        dataSource={tableData}
        columns={columns}
        pagination={false}
        size="small"
        showHeader
      />
    </div>
  );
}
